import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ShoppingCart, Plus, Package, Users, LayoutGrid, Trash2, X, Search, Camera, Receipt, Save,
  Minus, Image as ImageIcon, BarChart3, Settings, Printer, Clock, ArrowUpRight, Edit3,
  FileText, Download, AlertTriangle, Ban, UserCheck, Share2, Copy, AlertOctagon, RefreshCw, Truck,
  RotateCcw, ChevronLeft, ChevronRight, ScanBarcode, UserCircle2, Layers, Lock, LogIn, Menu, LogOut,
  HelpCircle, CheckSquare, Square, MapPin, Phone, PauseCircle, PlayCircle, AlertCircle, Banknote,
  CreditCard, Smartphone, BookOpen, Crown, Unlock, Globe, Wallet, History, FileCheck, Boxes, PenLine,
  CreditCard as CardIcon, Store, ArrowRightLeft, UserCog, Briefcase, FileOutput, Megaphone, UserPlus,
  Link, MonitorSmartphone, Contact, FileMinus } from 'lucide-react';
import { apiCall, generateId, formatPrice, formatDate, compressImage,
  base64ToFile, printHtmlDiv, printRawHtml, printBarcodeDiv } from '../utils/utils.js';

const ReturnModal = ({ order, onClose, onProcessReturn }) => {
    if (!order) return null;
    const [processing, setProcessing] = useState(false);
    const [returnType, setReturnType] = useState('Restock'); // 'Restock' or 'Damage'
    
    // Track the quantity selected to return for each item in the order
    const [returnSelections, setReturnSelections] = useState(() => 
        (order.items || []).map(item => ({ ...item, qtyToReturn: 0 }))
    );

    const updateReturnQty = (index, delta) => {
        setReturnSelections(prev => prev.map((item, i) => {
            if (i === index) {
                const newQty = item.qtyToReturn + delta;
                if (newQty >= 0 && newQty <= item.qty) {
                    return { ...item, qtyToReturn: newQty };
                }
            }
            return item;
        }));
    };

    const calculateTotalRefund = () => {
        return returnSelections.reduce((sum, item) => sum + (item.price * item.qtyToReturn), 0);
    };

    const handleProcess = async () => {
        const itemsToReturn = returnSelections.filter(i => i.qtyToReturn > 0);
        if (itemsToReturn.length === 0) {
            alert("Please select at least one item to return.");
            return;
        }
        
        setProcessing(true);
        const status = returnType === 'Restock' ? 'Returned-Restocked' : 'Returned-Damaged';
        
        // Create a negative payload specifically for the returned items
        const refundPayload = itemsToReturn.map(i => ({ ...i, qty: i.qtyToReturn }));
        
        await onProcessReturn(order, status, returnType === 'Restock', refundPayload, calculateTotalRefund());
        setProcessing(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-[#1a1a1a]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-md rounded-xl shadow-lg border border-[#e1e3e5] overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-[#e1e3e5] flex justify-between items-center bg-[#f6f7f8]">
                    <h3 className="font-bold text-[#1a1a1a] flex items-center gap-2"><FileMinus size={18} className="text-[#d82c0d]"/> Partial Customer Return</h3>
                    <button onClick={onClose} className="text-[#5c5f62]"><X size={20}/></button>
                </div>
                
                <div className="p-4 overflow-y-auto bg-white flex-1">
                    <p className="text-sm text-[#5c5f62] mb-4">Order #{order.id.slice(0,8).toUpperCase()} • {formatDate(order.createdAt)}</p>
                    
                    <div className="space-y-3 mb-6">
                        <label className="text-xs font-semibold text-[#5c5f62] uppercase block mb-1">Select Items to Return</label>
                        {returnSelections.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 border border-[#e1e3e5] rounded-lg bg-[#f6f7f8]">
                                <div className="flex-1 min-w-0 pr-2">
                                    <div className="font-semibold text-sm text-[#1a1a1a] truncate">{item?.name}</div>
                                    <div className="text-xs text-[#5c5f62]">
                                        {formatPrice(item.price)} each {item.variant && `(${item.variant})`} 
                                        <span className="ml-1 text-[#1a1a1a] font-medium">• Max: {item.qty}</span>
                                    </div>
                                </div>
                                <div className="flex items-center bg-white rounded border border-[#c9cccf] shadow-sm">
                                    <button type="button" onClick={() => updateReturnQty(idx, -1)} className="px-2 py-1 text-[#5c5f62] hover:bg-[#f6f7f8]"><Minus size={14}/></button>
                                    <span className="text-sm font-bold w-6 text-center text-[#1a1a1a]">{item.qtyToReturn}</span>
                                    <button type="button" onClick={() => updateReturnQty(idx, 1)} className="px-2 py-1 text-[#5c5f62] hover:bg-[#f6f7f8]"><Plus size={14}/></button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mb-6">
                        <label className="text-xs font-semibold text-[#5c5f62] uppercase block mb-2">Condition of Returned Items</label>
                        <div className="grid grid-cols-2 gap-3">
                            <label className={`flex flex-col items-center p-3 border rounded-lg cursor-pointer transition-colors ${returnType === 'Restock' ? 'border-[#008060] bg-[#e4fcf4] text-[#008060]' : 'border-[#c9cccf] bg-white text-[#5c5f62] hover:bg-[#f6f7f8]'}`}>
                                <input type="radio" className="hidden" checked={returnType === 'Restock'} onChange={() => setReturnType('Restock')} />
                                <RotateCcw size={20} className="mb-1"/>
                                <span className="font-semibold text-sm">Add to Stock</span>
                            </label>
                            <label className={`flex flex-col items-center p-3 border rounded-lg cursor-pointer transition-colors ${returnType === 'Damage' ? 'border-[#d82c0d] bg-[#fbeae5] text-[#d82c0d]' : 'border-[#c9cccf] bg-white text-[#5c5f62] hover:bg-[#f6f7f8]'}`}>
                                <input type="radio" className="hidden" checked={returnType === 'Damage'} onChange={() => setReturnType('Damage')} />
                                <AlertTriangle size={20} className="mb-1"/>
                                <span className="font-semibold text-sm">Damaged (Loss)</span>
                            </label>
                        </div>
                    </div>

                    <div className="bg-[#f6f7f8] p-3 rounded-lg border border-[#e1e3e5] flex justify-between items-center mb-2">
                        <span className="font-bold text-sm text-[#1a1a1a]">Total Refund Amount</span>
                        <span className="text-lg font-black text-[#d82c0d]">{formatPrice(calculateTotalRefund())}</span>
                    </div>
                </div>
                
                <div className="p-4 border-t border-[#e1e3e5] bg-white">
                    <button disabled={processing || calculateTotalRefund() === 0} onClick={handleProcess} className="w-full bg-[#d82c0d] text-white py-3 rounded-lg font-semibold shadow-sm hover:bg-[#bf260a] transition-colors disabled:opacity-50">Confirm Partial Return</button>
                </div>
            </div>
        </div>
    );
};

export default ReturnModal;
